import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BookingSummary } from '@/types';
import { useTemplate } from '@/contexts/TemplateContext';
import { formatDateInTimezone, formatInTimezone } from '@/utils/timezone';

interface BookingConfirmationProps {
  booking: BookingSummary | null;
  profileName: string;
  profileTimezone?: string;
  isPolling?: boolean;
  pollingCount?: number;
  currency?: string;
  onBookAnother: () => void;
  onViewDetails: () => void;
}

export const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  booking,
  profileName,
  profileTimezone,
  isPolling = false,
  pollingCount = 0,
  currency = 'EUR',
  onBookAnother,
  onViewDetails,
}) => {
  const { colors } = useTemplate();
  const { t } = useTranslation('booking');
  const primaryColor = colors.colors.primary;
  const backgroundColor = colors.colors.background;
  const cardBackgroundColor = backgroundColor;
  const textColor = colors.colors.text;
  const mutedColor = colors.colors.textMuted;

  const formatDate = (booking: BookingSummary | null) => {
    if (!booking) return '--';
    
    // Prefer booking_datetime if available (UTC ISO8601)
    if (booking.booking_datetime) {
      const date = new Date(booking.booking_datetime);
      if (!isNaN(date.getTime())) {
        // Display in provider's timezone if available, otherwise local
        if (profileTimezone) {
          return formatDateInTimezone(booking.booking_datetime, profileTimezone, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        }
        return date.toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }
    }
    
    // Fallback to booking_date
    if (!booking.booking_date) return '--';
    const date = new Date(booking.booking_date + 'T00:00:00');
    if (isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (booking: BookingSummary | null) => {
    if (!booking) return '--:--';
    
    // Prefer booking_datetime if available (UTC ISO8601)
    if (booking.booking_datetime) {
      const date = new Date(booking.booking_datetime);
      if (!isNaN(date.getTime())) {
        // Display in provider's timezone if available, otherwise local
        if (profileTimezone) {
          return formatInTimezone(booking.booking_datetime, profileTimezone, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });
        }
        return date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
      }
    }
    
    // Fallback to booking_time
    if (!booking.booking_time) return '--:--';
    const [hours, minutes] = booking.booking_time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const generateCalendarUrl = () => {
    if (!booking) return '#';
    
    // Use booking_datetime if available (UTC ISO8601)
    const startDate = booking.booking_datetime
      ? new Date(booking.booking_datetime)
      : new Date(`${booking.booking_date}T${booking.booking_time}:00`);
    
    const endDate = new Date(startDate.getTime() + (booking.duration_minutes ?? 60) * 60000);
    
    const formatICS = (d: Date) =>
      d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    
    const icsContent = `BEGIN:VCALENDAR
  VERSION:2.0
  BEGIN:VEVENT
  DTSTART:${formatICS(startDate)}
  DTEND:${formatICS(endDate)}
  SUMMARY:Appointment at ${profileName}
  DESCRIPTION:${(booking.services ?? []).map((s) => s.name).join(', ')}
  END:VEVENT
  END:VCALENDAR`;
    
    return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
  };

  // Handle null booking - show placeholder UI
  if (!booking) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-foreground">{t('confirmation.title')}</h2>
          <p className="text-muted-foreground mt-2">
            {t('confirmation.subtitle')}
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-sm border p-6">
          <h3 className="font-semibold text-foreground mb-4">{t('confirmation.appointmentDetails')}</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('confirmation.provider')}</span>
              <span className="font-medium text-foreground">{profileName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('confirmation.date')}</span>
              <span className="font-medium text-foreground">{t('confirmation.noBooking')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('confirmation.time')}</span>
              <span className="font-medium text-foreground">{t('confirmation.noBooking')}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onViewDetails}
            className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/70 transition-colors"
          >
            {t('confirmation.viewDetails')}
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={onBookAnother}
            className="text-primary hover:text-primary/80 font-medium"
          >
            {t('confirmation.bookAnother')}
          </button>
        </div>
      </div>
    );
  }

  // Polling state - show spinner while waiting for confirmation
  if (isPolling) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-t-transparent animate-spin"
            style={{ borderColor: `${primaryColor} transparent ${primaryColor} ${primaryColor}` }}
          />
          <h2 className="text-2xl font-bold" style={{ color: textColor }}>
            {t('confirmation.titleConfirming')}
          </h2>
          <p className="text-muted-foreground mt-2">
            {t('confirmation.subtitleConfirming', { pollingCount })}
          </p>
        </div>

        <div 
          className="rounded-lg shadow-sm border p-6"
          style={{ backgroundColor: cardBackgroundColor }}
        >
          <h3 className="font-semibold mb-4" style={{ color: textColor }}>
            {t('confirmation.appointmentDetails')}
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.provider')}</span>
              <span className="font-medium" style={{ color: textColor }}>{profileName}</span>
            </div>
            {booking && (
              <>
                <div className="flex justify-between">
                  <span style={{ color: mutedColor }}>{t('confirmation.date')}</span>
                  <span className="font-medium" style={{ color: textColor }}>
                    {formatDate(booking)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: mutedColor }}>{t('confirmation.time')}</span>
                  <span className="font-medium" style={{ color: textColor }}>
                    {formatTime(booking)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quota-hold queue state
  if (booking && booking.status === 'pending_quota_hold') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-bold" style={{ color: textColor }}>
            Request received
          </h2>
          <p className="text-muted-foreground mt-2">
            Your request has been received. The business will confirm your booking once availability opens.
          </p>
        </div>
        <div
          className="rounded-lg shadow-sm border p-6"
          style={{ backgroundColor: cardBackgroundColor }}
        >
          <h3 className="font-semibold mb-4" style={{ color: textColor }}>
            {t('confirmation.appointmentDetails')}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.provider')}</span>
              <span className="font-medium" style={{ color: textColor }}>{profileName}</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.date')}</span>
              <span className="font-medium" style={{ color: textColor }}>{formatDate(booking)}</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.time')}</span>
              <span className="font-medium" style={{ color: textColor }}>{formatTime(booking)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pending verification / confirmation state
  if (
    booking &&
    (booking.status === 'pending_verification' || booking.status === 'pending_confirmation')
  ) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">⏳</div>
          <h2 className="text-2xl font-bold" style={{ color: textColor }}>
            {booking.status === 'pending_confirmation'
              ? 'Pending provider confirmation'
              : t('confirmation.titlePending')}
          </h2>
          <p className="text-muted-foreground mt-2">
            {booking.status === 'pending_confirmation'
              ? 'Your booking is verified and waiting for the provider to confirm.'
              : t('confirmation.subtitlePending')}
          </p>
        </div>

        <div 
          className="rounded-lg shadow-sm border p-6"
          style={{ backgroundColor: cardBackgroundColor }}
        >
          <h3 className="font-semibold mb-4" style={{ color: textColor }}>
            {t('confirmation.appointmentDetails')}
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.provider')}</span>
              <span className="font-medium" style={{ color: textColor }}>{profileName}</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.date')}</span>
              <span className="font-medium" style={{ color: textColor }}>
                {formatDate(booking)}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.time')}</span>
              <span className="font-medium" style={{ color: textColor }}>
                {formatTime(booking)}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: mutedColor }}>{t('confirmation.duration')}</span>
              <span className="font-medium" style={{ color: textColor }}>
                {t('confirmation.durationMinutes', { minutes: booking.duration_minutes })}
              </span>
            </div>
            {booking.services && booking.services.length > 0 && (
              <div className="flex justify-between">
                <span style={{ color: mutedColor }}>{t('confirmation.services')}</span>
                <span className="font-medium text-right" style={{ color: textColor }}>
                  {booking.services.map((s) => s.name).join(', ')}
                </span>
              </div>
            )}
            {booking.total_price !== null && (
              <div className="flex justify-between pt-3 border-t">
                <span className="font-semibold" style={{ color: textColor }}>{t('confirmation.total')}</span>
                <span className="font-bold" style={{ color: primaryColor }}>
                  {new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: booking.currency || currency,
                  }).format(Number(booking.total_price || 0))}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onViewDetails}
            className="flex-1 px-6 py-3 rounded-lg font-medium text-center transition-colors"
            style={{ 
              backgroundColor: 'var(--muted, #f3f4f6)', 
              color: textColor 
            }}
          >
            {t('confirmation.viewDetails')}
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={onBookAnother}
            className="font-medium"
            style={{ color: primaryColor }}
          >
            {t('confirmation.bookAnother')}
          </button>
        </div>
      </div>
    );
  }

  // Confirmed state (default)
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-foreground">{t('confirmation.title')}</h2>
        <p className="text-muted-foreground mt-2">
          {t('confirmation.subtitle')}
        </p>
      </div>

      <div className="bg-card rounded-lg shadow-sm border p-6">
        <h3 className="font-semibold text-foreground mb-4">{t('confirmation.appointmentDetails')}</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('confirmation.provider')}</span>
            <span className="font-medium text-foreground">{profileName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('confirmation.date')}</span>
            <span className="font-medium text-foreground">
              {formatDate(booking)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('confirmation.time')}</span>
            <span className="font-medium text-foreground">
              {formatTime(booking)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('confirmation.duration')}</span>
            <span className="font-medium text-foreground">
              {t('confirmation.durationMinutes', { minutes: booking!.duration_minutes })}
            </span>
          </div>
          {booking.services && booking.services.length > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('confirmation.services')}</span>
              <span className="font-medium text-foreground text-right">
                {booking.services.map((s) => s.name).join(', ')}
              </span>
            </div>
          )}
          {booking!.total_price !== null && (
            <div className="flex justify-between pt-3 border-border">
              <span className="text-foreground font-semibold">{t('confirmation.total')}</span>
              <span className="font-bold text-primary">
                {new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: booking.currency || currency,
                }).format(Number(booking!.total_price || 0))}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
        <p className="text-sm text-green-600">
          <strong>✓</strong> {t('confirmation.emailSent', { email: booking!.customer_email })}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <a
          href={generateCalendarUrl()}
          download="appointment.ics"
          className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium text-center hover:bg-primary/90 transition-colors"
        >
          📅 {t('confirmation.addToCalendar')}
        </a>
        <button
          onClick={onViewDetails}
          className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/70 transition-colors"
        >
          {t('confirmation.viewDetails')}
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={onBookAnother}
          className="text-primary hover:text-primary/80 font-medium"
        >
          {t('confirmation.bookAnother')}
        </button>
      </div>
    </div>
  );
};
